import axios from "axios";
import { MakeAuthHeader, AtriumApiRoot, UserApi } from "../../config/api";
import AuthenticatedTechnician from "../../models/AuthenticatedTechnician";
import ApiCommunicationError from "../../exceptions/ApiCommunicationError";

// initial state
const state = {
  loading: false,
  error: false,
  userToken: null,
  userId: null,
  technicianId: null,
  authUser: null,
  loginError: null,
  editPasswordResponse: null,
  tokens: [],
  selectedAccount: null,
};

const getters = {
  loading(state) {
    return state.loading;
  },
  error(state) {
    return state.error;
  },
  userToken(state) {
    return state.userToken;
  },
  userId(state) {
    return state.userId;
  },
  authUser(state) {
    return state.authUser;
  },
  accounts(state) {
    if(state.authUser && state.authUser.accounts) {
      return state.authUser.accounts;
    }
    else {
      return [];
    }
  },
  loginError(state) {
    return state.loginError;
  },
  loggedIn(state) {
    return state.userToken !== null && state.userToken !== undefined;
  },
  editPasswordResponse(state) {
    return state.editPasswordResponse;
  },
  resetPaswordRequired(state) {
    return state.user != null ? state.user.forcePasswordReset : false;
  },
  technicianId(state) {
    return state.technicianId;
  },
  userApiToken(state) {
    const api = state.tokens.find(t => t.name == "UserApi");
    if (api != null) {
      return api.token;
    }
  },
  phocusEnigmaApiToken(state) {
    const api = state.tokens.find(t => t.name == "PhocusEnigmaApi");
    if (api != null) {
      return api.token;
    }
  },
  networkApiToken(state) {
    const api = state.tokens.find(t => t.name == "NetworkApi");
    if (api != null) {
      return api.token;
    }
  },
  poiApiToken(state) {
    const api = state.tokens.find(t => t.name == "PoiApi");
    if (api != null) {
      return api.token;
    }
  },
  settingApiToken(state) {
    const api = state.tokens.find(t => t.name == "SettingsApi");
    if (api != null) {
      return api.token;
    }
  },
  selectedAccount(state) {
    if(state.selectedAccount) {
      return state.selectedAccount
    }
    else {
      if(state.authUser && state.authUser.accounts) {
        return state.authUser.accounts[0];
      }
      else {
        return null;
      }
    }
  },
  isSuper(state) {
    if(state.authUser && state.authUser.role) {
      return state.authUser.role.followUp > 1;
    }
    else {
      return true;
    }
    
  }
};

// mutations
const mutations = {
  clearTokens(state) {
    state.tokens = [];
  },
  setTokens(state, payload) {
    state.tokens = [...payload];
  },
  setLoading(state, payload) {
    state.loading = payload;
  },
  clearLoading(state) {
    state.loading = false;
  },
  setError(state, payload) {
    state.error = payload;
  },
  clearError(state) {
    state.error = false;
  },
  setUserToken(state, payload) {
    state.userToken = payload;
  },
  clearUserToken(state) {
    state.userToken = null;
  },
  setUserId(state, payload) {
    state.userId = payload;
  },
  clearUserId(state) {
    state.userId = null;
  },
  setAuthUser(state, payload) {
    state.authUser = payload;
  },
  clearAuthUser(state) {
    state.authUser = null;
  },
  setLoginError(state, payload) {
    state.loginError = payload;
  },
  clearLoginError(state) {
    state.loginError = null;
  },
  setEditPasswordResponse(state, payload) {
    state.editPasswordResponse = payload;
  },
  clearEditPasswordResponse(state) {
    state.editPasswordResponse = null;
  },
  setTechnicianId(state, payload) {
    state.technicianId = payload;
  },
  setSelectedAccount(state, payload) {
    state.selectedAccount = payload;
  }
};

// actions
const actions = {
  login({ commit, getters }, { vm, username, password }) {
    let url = `${AtriumApiRoot}technician/`;
    commit("clearUserToken");
    commit("clearTokens");
    commit("clearUserId");
    commit("clearLoginError");
    commit("setLoading", true);
    axios
      .post(url, {
        UserName: username,
        Password: password
      })
      .then(res => {
        if (res.data) {
          let weekInMilliseconds = 1000 * 60 * 60 * 24 * 7;
          const { techAccountId, tokens, user, accounts, role } = res.data;
          if (user) {
            if(role && role.followUp == 0) {
              commit("setLoginError", "Access restricted by your account administrator");
            }
            else {
              commit("setTechnicianId", techAccountId);
              vm.$ls.set("technicianId", techAccountId, weekInMilliseconds);

              commit("setUserId", user.id);
              vm.$ls.set("user", user.id, weekInMilliseconds);

              let techUser = new AuthenticatedTechnician(user, accounts, role);
              vm.$ls.set("authUser", techUser, weekInMilliseconds);
              vm.$ls.set("userApi", techUser, weekInMilliseconds);
              commit("setAuthUser", techUser);

              vm.$ls.set("resetPassword", user.forcePassword, weekInMilliseconds);
              commit("setTokens", tokens);
              vm.$ls.set("tokens", tokens, weekInMilliseconds);
              const tokenObj = getters.userApiToken;
              
              if (tokenObj) {
                commit("setUserToken", tokenObj);
                vm.$ls.set("authToken", tokenObj, weekInMilliseconds);
              }
            }
          }
        } else {
          commit("setLoginError", "Email or Password incorrect");
        }
        commit("clearLoading");
      })
      .catch(() => {
        commit("setLoginError", "Email or Password incorrect");
        commit("clearLoading");
      });
  },
  logout({ commit, dispatch }) {
    dispatch("notifications/deleteMessageToken", null, { root: true });
    window.localStorage.clear();
    commit("clearUserToken");
    commit("clearAuthUser");
    commit("clearUserId");
    commit("clearTokens");
  },
  forgotPassword({ commit, rootGetters, dispatch }, { username, email }) {
    commit("clearError");
    commit("setLoading", true);
    commit("clearEditPasswordResponse");
    const url = rootGetters["page/atriumApi"];
    const data = {
      userName: username,
      email: email
    };
    let options = {
      url: url + "api/login/reset",
      method: "POST",
      data: data
    };
    axios(options)
      .then(res => {
        commit("setEditPasswordResponse", res.data);
        commit("clearLoading");
      })
      .catch(error => {
        commit("setError", error);
        commit("clearLoading");

        let err = new ApiCommunicationError(
          `Unable changes the users password. Inner error details: message: ${error.message}, name: ${error.name}, stack: ${error.stack}`
        );
        dispatch("errors/sendError", err, { root: true });
        dispatch(
          "errors/expectedErrorAlert",
          `Apologies, we where unable to issue you a password at this time. If you continue having issues please contact customer service.`,
          { root: true }
        );
      });
  },
  changePassword(
    { commit, state, dispatch },
    { vm, oldPassword, newPassword }
  ) {
    const token = state.userToken;
    let url = UserApi + "/api/login/updatepassword";
    const pwModel = {
      oldPassword,
      newPassword,
      id: state.userId
    };
    let options = {
      headers: MakeAuthHeader(token),
      url: url,
      method: "POST",
      data: pwModel
    };
    commit("clearError");
    commit("setLoading", true);
    commit("clearEditPasswordResponse");
    axios(options)
      .then(res => {
        if (res.data == "") {
          vm.$ls.set("resetPassword", false);
        } else {
          commit("setError", res.data);
        }
        commit("setEditPasswordResponse", res.data == "");
        commit("clearLoading");
      })
      .catch(error => {
        commit("setError", error);
        commit("clearLoading");

        let err = new ApiCommunicationError(
          `Unable changes the users password. Inner error details: message: ${error.message}, name: ${error.name}, stack: ${error.stack}`
        );
        dispatch("errors/sendError", err, { root: true });
        dispatch(
          "errors/expectedErrorAlert",
          `Apologies, we where unable to change your password at this time. If you continue having issues please contact customer service.`,
          { root: true }
        );
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
